import { useCallback, useEffect, useMemo } from "react";
import { Router, Route, Switch, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { CircularProgress, ThemeProvider } from "@mui/material";
import { lazy, Suspense } from "react";

// Non-dynamic imports
import { authAction } from "./actions";
import browserHistory from "./utils/browserHistory";
import pharmacyTheme from "./pages/PharmacyPartnerHome/pharmacyTheme";
import useCurrentPartnerDetails from "./hooks/useCurrentPartnerDetails";
import useCurrentUserRole from "./hooks/useCurrentUserRole";
import useHubspotTracking from "./hooks/useHubspotTracking";
import useAppSettings from "./hooks/useAppSettings";
import useOneSignal from "./customHooks/useOneSignal";
import Mixpanel from "./utils/Mixpanel";
import AuthOnlyRoute from "./components/common/AuthOnlyRoute";
import NoAuthOnlyRoute from "./components/common/NoAuthOnlyRoute";
import MustPassTestRoute from "./components/common/MustPassTestRoute";
import NotificationsPermissionAlert from "./components/NotificationsPermissionAlert";
import ErrorBoundary from "./components/ErrorBoundary";
import ForceUserLogout from "./components/ForceUserLogout";
import { Toaster } from "sonner";
import "./newborn/newborn.css";
import { HOME_NURSING_BASE_URL, HOME_NURSING_IS_DEV } from "./newborn/api/homeNursingClient";
import { shouldShowHomeNursingRevenueTracker } from "./newborn/lib/constants/vendor-mapping";
import ReduxStateModel from "./models/ReduxStateModel";

// Lazy loaded components
import { pdfjs } from "react-pdf";
const AllBookings = lazy(() => import("./pages/AllBookings"));
const PharmacySettings = lazy(() => import("./pages/PharmacySettings"));
const PharmacyPartnerHome = lazy(() => import("./pages/PharmacyPartnerHome"));
const FA2Login = lazy(() => import("./pages/FA2Login"));
const UserSignUpLink = lazy(() => import("./pages/UserSignUpLink"));
const NewLoginPage = lazy(
  () => import("./components/LoginSignup/NewLoginPage")
);
const Signup = lazy(() => import("./pages/Signup/NewSignupPage"));
const Settings = lazy(() => import("./pages/Settings"));
const ShowOrder = lazy(() => import("./pages/ShowOrder"));
const SchedulerExploreView = lazy(() => import("./pages/SchedulerExploreView"));
const NewTeamPage = lazy(() => import("./pages/NewTeamPage"));
const NewDriverSchedule = lazy(() => import("./pages/NewDriverSchedule"));
const NewNurseProfile = lazy(() => import("./pages/NewNurseProfile"));
const InactiveNurseProfile = lazy(() => import("./pages/NewNurseProfile/InactiveNurseProfile"));
const NewDriverProfile = lazy(() => import("./pages/NewDriverProfile"));
const Traction = lazy(() => import("./pages/Traction"));
const StreamVideoCall = lazy(() => import("./pages/StreamVideoCall"));
const DeliveryPartnerRevenue = lazy(
  () => import("./pages/DeliveryPartnerRevenue")
);
const CarePlan = lazy(() => import("./pages/CarePlan"));
const Caregivers = lazy(() => import("./pages/Caregivers"));
const RevenueTracker = lazy(() => import("./pages/RevenueTracker"));

// Newborn pages (no auth required)
const NewbornLayout = lazy(() => import("./newborn/components/layout/NewbornLayout"));
// Import Calendar directly (not lazy) so we can show custom skeleton loader
import NewbornCalendar from "./newborn/pages/Calendar";
const NewbornNurses = lazy(() => import("./newborn/pages/Nurses"));
const NewbornReports = lazy(() => import("./newborn/pages/Reports"));
const NewbornNotifications = lazy(() => import("./newborn/pages/Notifications"));

// Commented out imports that you can uncomment if needed
// const PharmacyRevenueTracker = lazy(() => import("./pages/PharmacyRevenueTracker"));

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.js",
  import.meta.url
).toString();

function App() {
  const dispatch = useDispatch();
  const { canUpdate } = useCurrentUserRole();
  const partnerUserUUID = localStorage?.partnerUserUUID;

  const { userName, phoneNumber, partnerUUID: partnerUUIDFromLocalStorage } = useMemo(() => {
    try {
      return localStorage?.setting ? JSON?.parse(localStorage?.setting) : {};
    } catch (e) {
      return {};
    }
  }, []);

  useEffect(() => {
    // localStorage.getItem("newAuthLogic")
    if (localStorage.getItem("newAuthLogic") && phoneNumber) {
      dispatch(
        authAction.getUserData({ userName: userName, phoneNumber: phoneNumber })
      );
    } else if (userName) {
      dispatch(
        authAction.getUserData({ userName: userName, phoneNumber: phoneNumber })
      );
    }
  }, [dispatch, phoneNumber, userName]);

  useEffect(() => {
    if (!partnerUserUUID) return;
    Mixpanel.identify(partnerUserUUID);
    if (userName) {
      Mixpanel.people.set({ name: userName });
    }
  }, [partnerUserUUID, userName]);

  useOneSignal();
  useHubspotTracking();
  useAppSettings();

  const { queryData, queryEnabled } = useCurrentPartnerDetails();
  const { query } = queryData;
  const partnerUUIDFromState = useSelector((state: ReduxStateModel) => state.authReducer?.setting?.partnerUUID);
  const partnerUUID = partnerUUIDFromState ?? partnerUUIDFromLocalStorage;
  const hostname = typeof window !== "undefined" ? window.location.hostname : "";
  const canAccessHomeNursingRevenueTracker = shouldShowHomeNursingRevenueTracker({
    partnerUUID,
    hostname,
    apiBaseUrl: HOME_NURSING_BASE_URL,
    isDev: HOME_NURSING_IS_DEV,
  });

  const checkIfIsNotDeliveryPartner = useCallback(() => {
    const value = !(query.data?.partner?.isDeliveryPartner ?? false);
    return value;
  }, [query.data?.partner?.isDeliveryPartner]);

  const checkIfPharmacyPartner = useCallback(() => {
    const value = query.data?.partner?.type === "PHARMACY";
    return value;
  }, [query.data?.partner?.type]);

  const showLoadingOverlay = query.isLoading && queryEnabled;

  // Check if we're on pages that use their own skeleton loader
  const isCalendarPage = window.location.pathname === '/newborn/calendar';
  const isCarePlanPage = window.location.pathname === '/care-plan';
  const isRevenueTrackerPage = window.location.pathname === '/revenue-tracker';
  const shouldShowLoadingOverlay = showLoadingOverlay && !isCalendarPage && !isCarePlanPage && !isRevenueTrackerPage;

  const NotificationsPermissionAlertGate = () => {
    const location = useLocation();
    if (location.pathname === "/care-plan" || location.pathname === "/revenue-tracker") {
      return null;
    }
    return <NotificationsPermissionAlert />;
  };

  return (
    <div className={`app ${shouldShowLoadingOverlay ? "app-loading" : ""}`.trim()}>
      <div className="loading-overlay">
        {shouldShowLoadingOverlay && <CircularProgress />}
      </div>
      <ErrorBoundary>
        <Router history={browserHistory}>
          <ForceUserLogout />
          <NotificationsPermissionAlertGate />
          <Suspense
            fallback={
              <div className="suspense-loading">
                <CircularProgress />
              </div>
            }
          >
            <Switch>
              <Route
                exact={true}
                path={"/pharmacy-settings"}
                render={() => {
                  return (
                    <MustPassTestRoute
                      test={checkIfPharmacyPartner}
                      queryLoading={query.isLoading}
                      child={
                        <ThemeProvider theme={pharmacyTheme}>
                          <PharmacySettings />
                        </ThemeProvider>
                      }
                    />
                  );
                }}
              />
              <Route
                path={"/video-call/:id/:userUUID"}
                exact={true}
                render={() => {
                  return (
                    <AuthOnlyRoute
                      child={
                        <MustPassTestRoute
                          child={<StreamVideoCall />}
                          test={checkIfIsNotDeliveryPartner}
                          queryLoading={query.isLoading}
                        />
                      }
                    />
                  );
                }}
              />
              <Route
                exact={false}
                path="/pharmacy"
                render={() => {
                  return (
                    <AuthOnlyRoute
                      child={
                        <ThemeProvider theme={pharmacyTheme}>
                          <MustPassTestRoute
                            child={<PharmacyPartnerHome />}
                            test={checkIfPharmacyPartner}
                            queryLoading={query.isLoading}
                          />
                        </ThemeProvider>
                      }
                    />
                  );
                }}
              />
              <Route
                exact={true}
                path="/traction"
                render={() => {
                  return (
                    <AuthOnlyRoute
                      child={
                        <MustPassTestRoute
                          child={
                            <MustPassTestRoute
                              child={<Traction />}
                              test={() => !checkIfPharmacyPartner()}
                              queryLoading={query.isLoading}
                            />
                          }
                          test={checkIfIsNotDeliveryPartner}
                          queryLoading={query.isLoading}
                        />
                      }
                    />
                  );
                }}
              />
              <Route
                path="/new-driver-schedule"
                render={() => {
                  return (
                    <AuthOnlyRoute
                      child={
                        <MustPassTestRoute
                          queryLoading={query.isLoading}
                          redirectPath="/pharmacy"
                          test={() => !checkIfPharmacyPartner()}
                          child={
                            <MustPassTestRoute
                              child={<NewDriverSchedule />}
                              test={checkIfIsNotDeliveryPartner}
                              queryLoading={query.isLoading}
                            />
                          }
                        />
                      }
                    />
                  );
                }}
              />
              <Route
                path="/auto-generated-driver-schedule"
                render={() => {
                  return (
                    <AuthOnlyRoute
                      child={
                        <MustPassTestRoute
                          test={() => !checkIfPharmacyPartner()}
                          redirectPath="/pharmacy"
                          queryLoading={query.isLoading}
                          child={
                            <MustPassTestRoute
                              child={<NewDriverSchedule />}
                              test={checkIfIsNotDeliveryPartner}
                              queryLoading={query.isLoading}
                            />
                          }
                        />
                      }
                    />
                  );
                }}
              />
              <Route
                path="/scheduler"
                render={() => {
                  return (
                    <AuthOnlyRoute
                      child={
                        <MustPassTestRoute
                          test={() => !checkIfPharmacyPartner()}
                          redirectPath="/pharmacy"
                          queryLoading={query.isLoading}
                          child={
                            <MustPassTestRoute
                              child={<SchedulerExploreView />}
                              test={checkIfIsNotDeliveryPartner}
                              queryLoading={query.isLoading}
                            />
                          }
                        />
                      }
                    />
                  );
                }}
              />
              <Route
                exact={true}
                path={"/sign-up-link/:userData"}
                render={() => {
                  return <UserSignUpLink />;
                }}
              />
              <Route
                exact={true}
                path={"/login"}
                render={() => {
                  return <NoAuthOnlyRoute child={<FA2Login />} />;
                }}
              />
              <Route
                exact
                path="/legacy-login"
                render={() => {
                  return <NoAuthOnlyRoute child={<NewLoginPage />} />;
                }}
              />
              <Route
                exact
                path="/signup"
                render={() => {
                  return <NoAuthOnlyRoute child={<Signup />} />;
                }}
              />
              <Route
                exact
                path="/settings"
                render={() => {
                  return (
                    <AuthOnlyRoute
                      child={
                        <MustPassTestRoute
                          child={<Settings />}
                          test={() => !checkIfPharmacyPartner()}
                          redirectPath="/pharmacy-settings"
                          queryLoading={query.isLoading}
                        />
                      }
                    />
                  );
                }}
              />
              <Route
                exact
                path="/team"
                render={() => {
                  return (
                    <AuthOnlyRoute
                      child={
                        <MustPassTestRoute
                          child={<NewTeamPage />}
                          queryLoading={query.isLoading}
                          test={() => {
                            const isNotDeliveryPartner =
                              checkIfIsNotDeliveryPartner();
                            return (
                              !checkIfPharmacyPartner() && isNotDeliveryPartner
                            );
                          }}
                          redirectPath="/pharmacy"
                        />
                      }
                    />
                  );
                }}
              />
              <Route
                path="/order/:orderUUID"
                render={() => {
                  return (
                    <AuthOnlyRoute
                      child={
                        <MustPassTestRoute
                          queryLoading={query.isLoading}
                          redirectPath="/pharmacy"
                          test={checkIfIsNotDeliveryPartner}
                          child={
                            <MustPassTestRoute
                              child={<ShowOrder />}
                              queryLoading={query.isLoading}
                              test={() => !checkIfPharmacyPartner()}
                              redirectPath="/pharmacy"
                            />
                          }
                        />
                      }
                    />
                  );
                }}
              />
              <Route
                exact
                path="/profile/professional/:id"
                render={() => {
                  return (
                    <AuthOnlyRoute
                      child={
                        <MustPassTestRoute
                          queryLoading={query.isLoading}
                          test={() => !checkIfPharmacyPartner()}
                          redirectPath="/pharmacy"
                          child={
                            <MustPassTestRoute
                              child={<NewNurseProfile />}
                              queryLoading={query.isLoading}
                              test={checkIfIsNotDeliveryPartner}
                            />
                          }
                        />
                      }
                    />
                  );
                }}
              />
              <Route
                exact
                path="/profile/professional/inactive/:id"
                render={() => {
                  return (
                    <AuthOnlyRoute
                      child={
                        <MustPassTestRoute
                          queryLoading={query.isLoading}
                          test={() => !checkIfPharmacyPartner()}
                          redirectPath="/pharmacy"
                          child={
                            <MustPassTestRoute
                              child={<InactiveNurseProfile />}
                              queryLoading={query.isLoading}
                              test={checkIfIsNotDeliveryPartner}
                            />
                          }
                        />
                      }
                    />
                  );
                }}
              />
              <Route
                exact
                path="/profile/driver/:id"
                render={() => {
                  return (
                    <AuthOnlyRoute
                      child={
                        <MustPassTestRoute
                          queryLoading={query.isLoading}
                          test={() => !checkIfPharmacyPartner()}
                          redirectPath="/pharmacy"
                          child={
                            <MustPassTestRoute
                              child={<NewDriverProfile />}
                              queryLoading={query.isLoading}
                              test={checkIfIsNotDeliveryPartner}
                            />
                          }
                        />
                      }
                    />
                  );
                }}
              />
              <Route
                exact
                path="/create-professional"
                render={() => {
                  return (
                    <AuthOnlyRoute
                      child={
                        <MustPassTestRoute
                          queryLoading={query.isLoading}
                          test={() => !checkIfPharmacyPartner()}
                          redirectPath="/pharmacy"
                          child={
                            <MustPassTestRoute
                              child={
                                <MustPassTestRoute
                                  child={<NewNurseProfile />}
                                  queryLoading={query.isLoading}
                                  test={checkIfIsNotDeliveryPartner}
                                />
                              }
                              test={() => canUpdate}
                              queryLoading={false}
                            />
                          }
                        />
                      }
                    />
                  );
                }}
              />
              <Route
                exact
                path="/create-driver"
                render={() => {
                  return (
                    <AuthOnlyRoute
                      child={
                        <MustPassTestRoute
                          queryLoading={query.isLoading}
                          test={() => !checkIfPharmacyPartner()}
                          redirectPath="/pharmacy"
                          child={
                            <MustPassTestRoute
                              child={
                                <MustPassTestRoute
                                  child={<NewDriverProfile />}
                                  queryLoading={query.isLoading}
                                  test={checkIfIsNotDeliveryPartner}
                                />
                              }
                              test={() => canUpdate}
                              queryLoading={false}
                            />
                          }
                        />
                      }
                    />
                  );
                }}
              />
              <Route
                exact={true}
                path="/delivery-partner-revenue"
                render={() => {
                  return (
                    <AuthOnlyRoute
                      child={
                        <MustPassTestRoute
                          test={() => !checkIfIsNotDeliveryPartner()}
                          child={<DeliveryPartnerRevenue />}
                          queryLoading={false}
                        />
                      }
                    />
                  );
                }}
              />
              <Route
                exact={true}
                path="/care-plan"
                render={() => {
                  return (
                    <AuthOnlyRoute
                      child={
                        <MustPassTestRoute
                          child={<CarePlan />}
                          queryLoading={query.isLoading}
                          redirectPath="/pharmacy"
                          test={() => !checkIfPharmacyPartner()}
                        />
                      }
                    />
                  );
                }}
              />
              <Route
                exact={true}
                path="/revenue-tracker"
                render={() => {
                  return (
                    <AuthOnlyRoute
                      child={
                        <MustPassTestRoute
                          child={
                            <MustPassTestRoute
                              child={<RevenueTracker />}
                              queryLoading={query.isLoading}
                              redirectPath="/care-plan"
                              test={() => canAccessHomeNursingRevenueTracker}
                            />
                          }
                          queryLoading={query.isLoading}
                          redirectPath="/pharmacy"
                          test={() => !checkIfPharmacyPartner()}
                        />
                      }
                    />
                  );
                }}
              />
              <Route
                exact={true}
                path="/caregivers"
                render={() => {
                  return (
                    <AuthOnlyRoute
                      child={
                        <MustPassTestRoute
                          child={<Caregivers />}
                          queryLoading={query.isLoading}
                          redirectPath="/pharmacy"
                          test={() => !checkIfPharmacyPartner()}
                        />
                      }
                    />
                  );
                }}
              />
              <Route
                exact={true}
                path="/"
                render={() => {
                  return (
                    <AuthOnlyRoute
                      child={
                        <MustPassTestRoute
                          child={<AllBookings />}
                          queryLoading={query.isLoading}
                          redirectPath="/pharmacy"
                          test={() => !checkIfPharmacyPartner()}
                        />
                      }
                    />
                  );
                }}
              />

              {/* Newborn routes - no auth required */}
              <Route
                exact
                path="/newborn"
                render={() => (
                  <NewbornLayout>
                    <NewbornCalendar />
                  </NewbornLayout>
                )}
              />
              <Route
                exact
                path="/newborn/calendar"
                render={() => (
                  <div className="newborn">
                    <Toaster position="top-right" richColors />
                    <NewbornCalendar />
                  </div>
                )}
              />
              <Route
                exact
                path="/newborn/nurses"
                render={() => (
                  <NewbornLayout>
                    <NewbornNurses />
                  </NewbornLayout>
                )}
              />
              <Route
                exact
                path="/newborn/reports"
                render={() => (
                  <NewbornLayout>
                    <NewbornReports />
                  </NewbornLayout>
                )}
              />
              <Route
                exact
                path="/newborn/notifications"
                render={() => (
                  <NewbornLayout>
                    <NewbornNotifications />
                  </NewbornLayout>
                )}
              />
            </Switch>
          </Suspense>
        </Router>
      </ErrorBoundary>
    </div>
  );
}

export default App;
